import type { SchemaInput, SchemaRecord } from './schema-input.js';
import { isString, isNumber, isBoolean, isSchemaRecord } from './runtime-values.js';
import { requestErrorDetails } from './request-error.js';
import { ANALYTICS_ERROR_BLOB_MAX_LENGTH } from './analytics-schema.js';

/** Error text can contain credentials even when request bodies are not recorded. */
export function cleanErrorText(value: string, limit = 2200): string {
  return value
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/giu, '<redacted-email>')
    .replace(/\b(?:sk_(?:live|test)_[A-Za-z0-9]+|whsec_[A-Za-z0-9]+)\b/gu, '<redacted-secret>')
    .replace(/(\bBearer\s+)[A-Za-z0-9._~+/=-]+/giu, '$1<redacted>')
    .replace(/(https?:\/\/[^\s?#)]+)[?#][^\s)]*/gu, '$1')
    .replace(/([?&](?:access_token|code|key|secret|token)=)[^&\s]+/giu, '$1<redacted>')
    .slice(0, limit);
}

export function errorReport(cause: SchemaInput, fallback: string) {
  const causes: { name: string; message: string; stack_head: string }[] = [];
  const seen = new Set<Error>();
  let current = cause;
  while (current instanceof Error && !seen.has(current) && seen.size < 4) {
    seen.add(current);
    causes.push({ name: cleanErrorText(current.name, 80), message: cleanErrorText(current.message, 500), stack_head: cleanErrorText(current.stack ?? '', 2200) });
    current = current.cause;
  }
  return {
    schema_version: 1,
    ...requestErrorDetails(cause),
    name: causes[0]?.name ?? 'Error',
    message: causes[0]?.message || cleanErrorText(isString(cause) ? cause : fallback, 500),
    stack_head: causes[0]?.stack_head ?? '',
    causes: causes.slice(1),
  };
}

/** Keep valid JSON and correlation fields when optional evidence exceeds storage. */
export function serializeErrorReport(report: SchemaRecord): string {
  try { return encodeErrorReport(report); }
  catch { return JSON.stringify({ schema_version: 1, kind: 'serialization_error', message: 'Failed to serialize error report', truncated: true }); }
}

function encodeErrorReport(report: SchemaRecord): string {
  const json = JSON.stringify(report, (_key, value: SchemaInput) => isString(value) ? cleanErrorText(value) : value);
  const parsed: SchemaInput = JSON.parse(json);
  if (!isSchemaRecord(parsed)) return JSON.stringify({ schema_version: 1, message: 'Invalid error report' });
  const payload = parsed;
  payload.schema_version = 1;
  let encoded = JSON.stringify(payload);
  if (encoded.length <= ANALYTICS_ERROR_BLOB_MAX_LENGTH) return encoded;
  payload.truncated = true;
  // Reduce long nested stacks first; retain their cause names and messages.
  if (Array.isArray(payload.causes)) for (const cause of payload.causes) { if (isSchemaRecord(cause)) cause.stack_head = String(cause.stack_head ?? '').slice(0, 400); }
  for (const limit of [1600, 1000, 500]) {
    if (isString(payload.stack_head)) payload.stack_head = payload.stack_head.slice(0, limit);
    if (Array.isArray(payload.actions)) payload.actions = payload.actions.slice(-6);
    encoded = JSON.stringify(payload);
    if (encoded.length <= ANALYTICS_ERROR_BLOB_MAX_LENGTH) return encoded;
  }
  const essential = Object.fromEntries(['schema_version', 'kind', 'name', 'message', 'operation', 'source', 'request_id', 'app_version', 'worker_version', 'trace_id', 'span_id', 'http_status', 'status', 'failure_code', 'failure_kind', 'expected_outcome', 'cause_type', 'service', 'stage', 'page_path', 'language', 'request_trigger', 'elapsed_ms', 'online', 'visibility', 'response_received', 'response_type', 'stack_head', 'truncated']
    .filter(key => isString(payload[key]) || isNumber(payload[key]) || isBoolean(payload[key])).map(key => [key, isString(payload[key]) ? payload[key].slice(0, key === 'stack_head' ? 500 : 160) : payload[key]]));
  const causes = Array.isArray(payload.causes) ? payload.causes.filter(isSchemaRecord).slice(0, 3)
    .map(cause => ({ name: String(cause.name ?? 'Error').slice(0, 80), message: String(cause.message ?? '').slice(0, 120) })) : [];
  const withCauses = JSON.stringify({ ...essential, causes });
  return withCauses.length <= ANALYTICS_ERROR_BLOB_MAX_LENGTH ? withCauses : JSON.stringify(essential);
}


/** Request exception messages can quote private responses. Keep locations and types. */
export function requestFailureReport(cause: SchemaInput) {
  const report = errorReport(cause, 'Request failed');
  const frames = (stack: string) => stack.split('\n').filter(line => /^\s*at\s/.test(line) || /@.*:\d/.test(line)).join('\n');
  return { ...report, message: 'Request failed', stack_head: frames(report.stack_head),
    causes: report.causes.map(item => ({ ...item, message: item.name, stack_head: frames(item.stack_head) })) };
}
